/**
 * One-time dismissible banner below the global header announcing the desktop
 * app. Design matches Figma (VTs/VAUs → "VT Set up"): brand-50 green tint,
 * Heading-4 copy, custom monitor illustration, inline "Download now" link.
 *
 * Dismissal is persisted per-browser via useLocalStorage — once closed it stays
 * closed. Self-hides inside the desktop shell (nothing to download there) and
 * while acting as a VAU (the "Acting as" banner owns that row).
 *
 * The link target is a temporary hardcoded support article; see
 * constants/desktop.ts for the dynamic-URL follow-up.
 */

import { IconDownload, IconXmark } from '@optiaxiom/icons';
import { Box, Button, Flex, Heading, Tooltip } from '@optiaxiom/react';

import monitorIcon from '@frontend/assets/desktop-app-monitor.svg';
import { DESKTOP_DOWNLOAD_URL } from '@frontend/constants/desktop';
import { useVAUContext } from '@frontend/features/vau/hooks/useVAUContext';
import { useLocalStorage } from '@frontend/hooks';
import { isDesktopApp } from '@frontend/utils/desktop';

const DISMISSED_KEY = 'opal.desktopDownloadBannerDismissed';

export const DesktopDownloadBanner = () => {
  const [dismissed, setDismissed] = useLocalStorage(DISMISSED_KEY, false);
  const { data, isError } = useVAUContext();

  // Hide while assumed — or, as a fail-safe, when the probe errored and we
  // can't rule out that the session is assumed — so this never stacks on top
  // of the "Acting as <VAU>" banner. Mirrors AssumedVAUBanner's own gate.
  const maybeAssumed = (data?.is_assumed ?? false) || isError;

  if (dismissed || isDesktopApp() || maybeAssumed) return null;

  return (
    <Box
      role="status"
      px="24"
      py="12"
      borderB="1"
      borderColor="border.default"
      bg="bg.accent.subtle"
    >
      <Flex flexDirection="row" alignItems="center" gap="8">
        <Box flex="1" />
        <Flex flexDirection="row" alignItems="center" style={{ gap: 80 }}>
          <Flex flexDirection="row" alignItems="center" gap="8">
            <img src={monitorIcon} alt="" width={24} height={26} style={{ flexShrink: 0 }} />
            <Heading asChild level="4" fontSize="sm" fontWeight="600" color="fg.default">
              <span>
                Opal is now available as a desktop app. Get a faster, native experience on macOS,
                Windows, and Linux
              </span>
            </Heading>
          </Flex>
          <Button asChild appearance="subtle" size="sm" addonBefore={<IconDownload />}>
            <a href={DESKTOP_DOWNLOAD_URL} rel="noreferrer" target="_blank">
              Download now
            </a>
          </Button>
        </Flex>
        <Flex flex="1" flexDirection="row" justifyContent="flex-end">
          <Tooltip content="Dismiss" delayDuration={0}>
            <Button
              appearance="subtle"
              size="sm"
              square
              icon={<IconXmark />}
              aria-label="Dismiss"
              onClick={() => setDismissed(true)}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Box>
  );
};
